import type { ParsedCsv } from "./types";

const BOM = "﻿";

/**
 * Detect the most likely delimiter by looking at the first 20 lines and
 * counting occurrences of each candidate (comma, semicolon, tab) outside of
 * quoted regions. Highest count wins; ties break in favour of comma, then
 * semicolon, then tab.
 */
function detectDelimiter(text: string): string {
  const candidates = [",", ";", "\t"] as const;
  const counts: Record<string, number> = { ",": 0, ";": 0, "\t": 0 };

  let linesSeen = 0;
  let inQuotes = false;
  for (let i = 0; i < text.length && linesSeen < 20; i++) {
    const ch = text[i]!;
    if (ch === '"') {
      // Toggle quote state. Handle the escaped `""` sequence by skipping the
      // next char so we don't flip-flop twice on the same token.
      if (inQuotes && text[i + 1] === '"') {
        i++;
        continue;
      }
      inQuotes = !inQuotes;
      continue;
    }
    if (inQuotes) continue;
    if (ch === "\n") {
      linesSeen++;
      continue;
    }
    if (ch === "," || ch === ";" || ch === "\t") {
      counts[ch] = (counts[ch] ?? 0) + 1;
    }
  }

  let best: string = ",";
  let bestCount = -1;
  for (const cand of candidates) {
    if ((counts[cand] ?? 0) > bestCount) {
      bestCount = counts[cand] ?? 0;
      best = cand;
    }
  }
  return best;
}

/**
 * Parse a CSV text. Hand-rolled so we can:
 *   - Strip a leading BOM
 *   - Auto-detect delimiter
 *   - Preserve newlines inside quoted fields
 *   - Collapse `""` inside quotes to a single `"`
 *   - Drop fully-empty rows
 *   - Pad or truncate rows whose cell count doesn't match the header row
 *     (padding with "" so downstream `mapRows` has a predictable shape)
 */
export function parseCsv(text: string): ParsedCsv {
  let src = text;
  if (src.startsWith(BOM)) {
    src = src.slice(BOM.length);
  }

  const delimiter = detectDelimiter(src);

  const allRows: string[][] = [];
  let currentRow: string[] = [];
  let currentCell = "";
  let inQuotes = false;

  for (let i = 0; i < src.length; i++) {
    const ch = src[i]!;

    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          // Escaped quote inside a quoted field.
          currentCell += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        currentCell += ch;
      }
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
      continue;
    }
    if (ch === delimiter) {
      currentRow.push(currentCell);
      currentCell = "";
      continue;
    }
    if (ch === "\r") {
      // Swallow CR; the LF below will terminate the row. If the file happens
      // to be CR-only (old Mac), treat CR like LF.
      if (src[i + 1] === "\n") continue;
      currentRow.push(currentCell);
      allRows.push(currentRow);
      currentRow = [];
      currentCell = "";
      continue;
    }
    if (ch === "\n") {
      currentRow.push(currentCell);
      allRows.push(currentRow);
      currentRow = [];
      currentCell = "";
      continue;
    }
    currentCell += ch;
  }

  // Flush trailing cell/row if there's no terminal newline.
  if (currentCell.length > 0 || currentRow.length > 0) {
    currentRow.push(currentCell);
    allRows.push(currentRow);
  }

  // Drop entirely-blank rows (all cells empty after trimming).
  const nonEmptyRows = allRows.filter((row) =>
    row.some((cell) => cell.trim() !== ""),
  );

  if (nonEmptyRows.length === 0) {
    return { headers: [], rows: [], delimiter };
  }

  const headers = nonEmptyRows[0]!.map((h) => h.trim());
  const width = headers.length;
  const dataRows = nonEmptyRows.slice(1).map((row) => {
    if (row.length === width) return row;
    if (row.length < width) {
      const padded = row.slice();
      while (padded.length < width) padded.push("");
      return padded;
    }
    return row.slice(0, width);
  });

  return { headers, rows: dataRows, delimiter };
}
